<template>
  <div :class="{ 'disabled-menu-item': isDisabled }" class="navigation-item-container">
    <div v-if="!hasSubMenu"
         :class="['stripe-nav-item', { 'active': isRouteActive(item.name) }]"
         @click="navigateTo(item)"
         v-show="shouldShow">
      <span class="stripe-nav-icon">
        <v-icon size="20">{{ item.icon }}</v-icon>
      </span>
      <span class="stripe-nav-title">{{ getItemLabel }}</span>
      <v-progress-circular
        v-if="isLoading"
        indeterminate
        size="16"
        width="2"
        color="white"
        class="ml-2 loading-indicator"
      ></v-progress-circular>
    </div>

    <div v-else 
         :class="['stripe-nav-group', { 'expanded': isExpanded }]" 
         v-show="shouldShow">
      <div class="stripe-nav-group-header" @click="toggleGroup">
        <span class="stripe-nav-icon">
          <v-icon size="20">{{ item.icon }}</v-icon>
        </span>
        <span class="stripe-nav-title">{{ getItemLabel }}</span>
        <span class="stripe-nav-arrow">
          <v-icon size="18">mdi-chevron-down</v-icon>
        </span>
      </div>

      <transition name="expand">
        <div class="stripe-submenu" v-show="isExpanded">
          <navigation-item
            v-for="(subItem, subKey) in item.subMenu"
            :key="`sub-${menuKey}-${subKey}`"
            :item="subItem"
            :menu-key="`${menuKey}-${subKey}`"
            :is-sub-group="true"
          />
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import PermissionService from '@/services/PermissionService'
import LanguajeService from '@/services/LanguajeService'
import { resolverEvents, getRouteResolver } from '@/router/route-resolvers'

export default {
  name: 'NavigationItem',

  props: {
    item: {
      type: Object,
      required: true
    },
    menuKey: {
      type: String,
      default: ''
    },
    isSubGroup: {
      type: Boolean,
      default: false
    },
    activeRoute: {
      type: String,
      default: ''
    },
    isDisabled: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      profile: {},
      isExpanded: false,
      isLoading: false,
      lastClickedRoute: null
    }
  },

  computed: {
    ...mapState('navigation', ['disabledMenuItems']),

    hasSubMenu() {
      return !!this.item.subMenu
    },

    isEnabled() {
      return !this.disabledMenuItems.includes(this.menuKey)
    },

    shouldShow() {
      return this.checkPermissions(this.item.permissionRequired)
    },

    isActiveGroup() {
      if (!this.item.subMenu) return false

      const currentPath = this.$route.path
      const basePath = this.item.path || 
                      (this.item.name ? `/${this.item.name}` : null)

      if (!basePath) {
        return Object.values(this.item.subMenu).some(subItem => {
          const subItemPath = subItem.path || 
                            (subItem.name ? `/${subItem.name}` : '')
          return currentPath.startsWith(subItemPath)
        })
      }

      return currentPath.startsWith(basePath)
    },

    getItemTo() {
      const profile = JSON.parse(localStorage.getItem('profile') || '{}')
      
      // Usar el menuKey o id como identificador único
      const itemId = this.item.menuKey || this.item.id;
      
      if (this.item.name === 'integrations') {
        return { 
          path: `/integrations/${(profile.company_owner || 'all').toLowerCase()}`
        }
      }
      if (this.item.name) {
        return { 
          name: this.item.name,
          params: { menuId: itemId } // Agregar el identificador como parámetro
        }
      }
      if (this.item.path) {
        return { 
          path: this.item.path,
          params: { menuId: itemId }
        }
      }
      return {}
    },

    getItemLabel() {
      if (this.item.language_key && Array.isArray(this.item.language_key)) {
        return this.translate(this.item.language_key);
      }
      if (this.item.translationKey) {
        return this.$t(this.item.translationKey);
      }
      if (this.item.translations) {
        const currentLang = LanguajeService.getLenguajeName();
        return this.item.translations[currentLang] || this.item.translations.ENGLISH;
      }
      return this.item.name || 'Unnamed Menu';
    }
  },

  methods: {
    translate(languageKey) {
      if (!Array.isArray(languageKey)) return '';
      const currentLang = LanguajeService.getLenguajeName();
      const translation = languageKey.find(l => l.language === currentLang);
      if (translation && translation.label) {
        return translation.label;
      }
      const englishTranslation = languageKey.find(l => l.language === 'ENGLISH');
      return englishTranslation ? englishTranslation.label : '';
    },
    checkPermissions(permissions) {
      if (!permissions || permissions.length === 0) return false

      if (this.hasSubMenu) {
        const subMenuItems = Object.values(this.item.subMenu || {})
        const accessibleChildren = subMenuItems.filter(subItem => {
          const subPerms = subItem.permissionRequired || []
          return PermissionService.check(subPerms)
        })
        return accessibleChildren.length > 0
      }

      return PermissionService.check(permissions)
    },

    getAllItemNames(item) {
      let names = []
      
      if (item.name) {
        names.push(item.name)
      }

      if (item.subMenu) {
        Object.values(item.subMenu).forEach(subItem => {
          names = names.concat(this.getAllItemNames(subItem))
        })
      }

      return names
    },

    isRouteActive(name) {
      const currentRoute = this.$route;
      
      if (this.item.isEditable) {
        // Para items editables, comparar por path
        return this.item.path === currentRoute.path;
      }

      // Para items del sistema, mantener la lógica existente
      return !this.item.isEditable && (
        name === currentRoute.name || 
        name === currentRoute.meta?.parent ||
        (name === 'roles' && currentRoute.name?.startsWith('roles-'))
      );
    },

    navigateTo(item) {
      if (!item.name && !item.path) return;
      
      const routeName = item.name;
      const routePath = item.path;
      
      // Verificar si ya estamos en esta ruta para evitar doble navegación
      const currentRouteName = this.$route.name;
      const currentPath = this.$route.path;
      
      if ((routeName && routeName === currentRouteName) || 
          (routePath && routePath === currentPath)) {
        console.log('Already on this route, skipping navigation');
        return;
      }

      // Prevent rapid double-clicking
      if (this.isLoading) {
        console.log('Navigation already in progress, skipping duplicate request');
        return;
      }
      
      // Verificar si la ruta tiene resolvers antes de mostrar el indicador
      const hasResolver = routeName && getRouteResolver(routeName);
      
      // Solo activamos el indicador si la ruta tiene resolvers registrados
      if (hasResolver) {
        this.isLoading = true;
        this.lastClickedRoute = routeName;
        console.log('Setting loading state for route:', routeName);
      }
      
      // Para items editables o con path explícito
      if (item.isEditable && routePath) {
        this.$router.push({
          path: routePath,
          query: { timestamp: Date.now() }
        }).catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            console.error('Navigation error:', err);
          }
          if (hasResolver) {
            this.isLoading = false;
            this.lastClickedRoute = null;
          }
        });
      } else {
        // Para items del sistema
        const route = this.getBaseRoute(item);
        
        if (route.path || route.name) {
          this.$router.push(route).catch(err => {
            if (err.name !== 'NavigationDuplicated') {
              console.error('Navigation error:', err);
            }
            if (hasResolver) {
              this.isLoading = false;
              this.lastClickedRoute = null;
            }
          });
        }
      }
    },

    getBaseRoute(item) {
      if (item.name === 'integrations') {
        return {
          path: `/integrations/${(this.profile.company_owner || 'all').toLowerCase()}`
        };
      }
      if (item.path) {
        return { path: item.path };
      }
      if (item.name) {
        return { name: item.name };
      }
      return {};
    },

    toggleGroup() {
      this.isExpanded = !this.isExpanded
    }
  },

  mounted() {
    this.$root.$on('update:company', profile => {
      this.profile = profile
    });
    
    // Escuchar eventos del sistema de resolvers
    resolverEvents.$on('resolver:start', ({ routeName }) => {
      // Sólo mostrar el indicador si este ítem corresponde a la ruta en cuestión
      if ((this.lastClickedRoute === routeName || this.item.name === routeName) 
          && getRouteResolver(routeName)) {
        this.isLoading = true;
      }
    });
    
    resolverEvents.$on('resolver:end', ({ routeName }) => {
      if ((this.lastClickedRoute === routeName || this.item.name === routeName) 
          && getRouteResolver(routeName)) {
        this.isLoading = false;
        this.lastClickedRoute = null;
      }
    });
    
    resolverEvents.$on('resolver:error', ({ routeName }) => {
      if ((this.lastClickedRoute === routeName || this.item.name === routeName) 
          && getRouteResolver(routeName)) {
        this.isLoading = false;
        this.lastClickedRoute = null;
      }
    });
    
    // Clear loading state after navigation is complete
    this.$router.afterEach((to, from) => {
      // Always reset loading state to ensure UI doesn't get stuck
      setTimeout(() => {
        this.isLoading = false;
        this.lastClickedRoute = null;
      }, 1500);
    });
  },

  beforeDestroy() {
    this.$root.$off('update:company');
    resolverEvents.$off('resolver:start');
    resolverEvents.$off('resolver:end');
    resolverEvents.$off('resolver:error');
  }
}
</script>

<style lang="scss" scoped>
.navigation-item-container {
  position: relative;
  margin-bottom: 1px;
}

.stripe-nav-item {
  display: flex;
  align-items: center;
  padding: 0 16px;
  height: 40px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--v-t-bw-secondary-base);
  opacity: 0.8;
  margin: 4px 8px;
  border-radius: 6px;
  position: relative;

  &:hover {
    background-color: rgba(255, 255, 255, 0.08);
    opacity: 1;
  }

  &.active {
    opacity: 1;
    background-color: rgba(255, 255, 255, 0.15);
  }
}

.loading-indicator {
  margin-left: auto !important;
}

.stripe-nav-icon {
  display: flex;
  align-items: center;
  margin-right: 12px;
  min-width: 24px;
  justify-content: center;
  
  .v-icon {
    font-size: 20px;
    color: inherit;
  }
}

.stripe-nav-title {
  flex: 1;
  font-size: 14px;
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: 0.1px;
}

.stripe-nav-group {
  margin: 4px 0;

  .stripe-nav-group-header {
    display: flex;
    align-items: center;
    padding: 0 16px;
    height: 40px;
    cursor: pointer;
    transition: all 0.2s ease;
    color: var(--v-t-bw-secondary-base);
    opacity: 0.8;
    margin: 2px 8px;
    border-radius: 6px;

    &:hover {
      background-color: rgba(255, 255, 255, 0.08);
      opacity: 1;
    }
  }

  .stripe-nav-arrow {
    margin-left: auto;
    transition: transform 0.3s ease;
    
    .v-icon {
      font-size: 18px;
      color: inherit;
    }
  }

  &.expanded {
    > .stripe-nav-group-header {
      opacity: 1;
      
      .stripe-nav-arrow {
        transform: rotate(-180deg);
      }
    }
  }
}

.stripe-submenu {
  padding-left: 16px;
  overflow: hidden;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    left: 8px;
    top: 0;
    height: 100%;
    width: 1px;
    background-color: rgba(255, 255, 255, 0.1);
  }
}

.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease-in-out;
  max-height: 500px; // Valor grande para permitir cualquier altura
  opacity: 1;
}

.expand-enter,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
  overflow: hidden;
}

.is-sub-group {
  .stripe-nav-item,
  .stripe-nav-group-header {
    padding-left: 12px;
    height: 36px;
    font-size: 13px;
  }
}

.disabled-menu-item {
  opacity: 0.5;
  pointer-events: none;
}
</style>
